import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SEMANTIC_BUCKETS } from "@/lib/statusSemantics";
import { TierBadge } from "@/components/presentation/TierBadge";
import { isDemoUser } from "@/lib/demoMode";
import { useAuth } from "@/context/AuthContext";

// v1.2 Part 2: "Daily Brief" — a short, conversational summary shown at
// the top of every dashboard (all roles). Answers "what matters today"
// before the user scrolls. Deterministic copy is generated server-side
// (see /app/backend/insights_engine.py); this component is purely
// presentational so a future AI-generated brief can drop in unchanged.
const scrollToAnchor = (anchor) => {
  window.setTimeout(() => {
    document.querySelector(`[data-anchor="${anchor}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, 250);
};

// Module-level constants (not recreated every render) — passed as-is into
// framer-motion's animation props below.
const BRIEF_MOTION_INITIAL = { opacity: 0, y: 8 };
const BRIEF_MOTION_ANIMATE = { opacity: 1, y: 0 };
const BRIEF_MOTION_TRANSITION = { duration: 0.18, ease: "easeOut" };

export const DailyBriefCard = ({ brief, loading }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const showTier = isDemoUser(user);

  const handleAction = (action) => {
    if (!action) return;
    navigate(action.route);
    if (action.anchor) scrollToAnchor(action.anchor);
  };

  if (loading) {
    return (
      <div
        className="relative overflow-hidden rounded-[var(--radius)] border border-l-4 border-l-[hsl(var(--role-accent))] bg-card p-4 sm:p-5"
        data-testid="daily-brief-card"
      >
        <Skeleton className="h-4 w-28 mb-3" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    );
  }

  if (!brief) return null;

  return (
    <motion.div
      initial={BRIEF_MOTION_INITIAL}
      animate={BRIEF_MOTION_ANIMATE}
      transition={BRIEF_MOTION_TRANSITION}
      className="relative overflow-hidden rounded-[var(--radius)] border border-l-4 border-l-[hsl(var(--role-accent))] bg-card text-card-foreground shadow-sm"
      data-testid="daily-brief-card"
    >
      <div className="bg-[hsl(var(--role-accent)/0.04)] px-4 sm:px-5 pt-4 pb-2 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <h2 className="font-heading text-base sm:text-lg font-semibold">Daily Brief</h2>
          {showTier && <TierBadge tier="professional" size="xs" />}
        </div>
        <span className="text-xs text-muted-foreground font-mono-ui shrink-0 mt-0.5" data-testid="daily-brief-timestamp">
          Updated just now
        </span>
      </div>
      <div className="px-4 sm:px-5 pb-4 sm:pb-5">
        <p className="mt-2 text-sm leading-relaxed text-foreground" data-testid="daily-brief-summary">
          {brief.summary}
        </p>

        {brief.bullets?.length > 0 && (
          <div className="mt-3 space-y-2">
            {brief.bullets.map((b) => {
              const sem = SEMANTIC_BUCKETS[b.bucketKey] || SEMANTIC_BUCKETS.waiting;
              return (
                <div key={b.label} className="flex items-start gap-2" data-testid="daily-brief-bullet">
                  <span className={`mt-1.5 h-2.5 w-2.5 rounded-full shrink-0 ${sem.dot}`} />
                  <span className="text-sm text-foreground">{b.label}</span>
                </div>
              );
            })}
          </div>
        )}

        {(brief.primary_action || brief.secondary_action) && (
          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
            {brief.primary_action && (
              <Button
                className="w-full sm:w-auto active:scale-[0.98]"
                onClick={() => handleAction(brief.primary_action)}
                data-testid="daily-brief-primary-action-button"
              >
                {brief.primary_action.label}
              </Button>
            )}
            {brief.secondary_action && (
              <Button
                variant="ghost"
                className="w-full sm:w-auto"
                onClick={() => handleAction(brief.secondary_action)}
                data-testid="daily-brief-secondary-action-button"
              >
                {brief.secondary_action.label}
              </Button>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};
